import { useState, useEffect, useMemo } from 'react';
import { useBroker } from '../contexts/BrokerContext';
import { useLocale } from '../contexts/LocaleContext';
import {
  TrendingUp, TrendingDown, Clock, Search, X, Settings, Loader, AlertCircle,
  ChevronDown, ChevronLeft, ChevronRight, SlidersHorizontal, Wifi, WifiOff, Activity, Layers, ShieldAlert,
} from 'lucide-react';
import toast from 'react-hot-toast';

// ─── Mobile detection ─────────────────────────────────────────────────────────
function useIsMobile(bp = 768) {
  const [is, setIs] = useState(window.innerWidth < bp);
  useEffect(() => {
    const h = () => setIs(window.innerWidth < bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return is;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function sideStyle(side) {
  return side === 'buy'
    ? { bg: 'var(--accent-success)', color: 'var(--accent-success)' }
    : { bg: 'var(--accent-danger)', color: 'var(--accent-danger)' };
}

function fmt(n, dp = 2) {
  return parseFloat(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  });
}

function pnlColor(v) {
  return parseFloat(v || 0) >= 0 ? 'var(--accent-success)' : 'var(--accent-danger)';
}

function formatDuration(openedAt, closedAt) {
  if (!openedAt || !closedAt) return '—';
  const secs = Math.floor((new Date(closedAt) - new Date(openedAt)) / 1000);
  if (secs < 0) return '—';
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ${secs % 60}s`;
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return `${h}h ${m}m`;
}

const PAGE_SIZE = 10; // trader groups per page

// ─── Pagination component ─────────────────────────────────────────────────────
function Pagination({ page, totalPages, total, pageSize, onChange }) {
  const { t } = useLocale();
  if (totalPages <= 1) return null;
  const pages = [];
  const delta = 2;
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - delta && i <= page + delta)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== '...') {
      pages.push('...');
    }
  }
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return (
    <div className="flex items-center justify-between px-6 py-4 border-t"
      style={{ borderColor: 'var(--border-color)' }}>
      <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
        {t('trading.showingOf', { from, to, total })}
      </p>
      <div className="flex items-center gap-1">
        <button onClick={() => onChange(page - 1)} disabled={page === 1}
          className="p-1.5 rounded-lg transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          style={{ color: 'var(--text-secondary)' }}>
          <ChevronLeft className="w-4 h-4" />
        </button>
        {pages.map((p, i) =>
          p === '...' ? (
            <span key={`el-${i}`} className="px-2 text-sm select-none" style={{ color: 'var(--text-tertiary)' }}>…</span>
          ) : (
            <button key={p} onClick={() => onChange(p)}
              className="w-8 h-8 rounded-lg text-sm font-semibold transition-all"
              style={{
                backgroundColor: p === page ? 'var(--accent-primary)' : 'transparent',
                color: p === page ? '#fff' : 'var(--text-secondary)',
              }}>
              {p}
            </button>
          )
        )}
        <button onClick={() => onChange(page + 1)} disabled={page === totalPages}
          className="p-1.5 rounded-lg transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          style={{ color: 'var(--text-secondary)' }}>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ─── Margin alert severity ────────────────────────────────────────────────────
function marginAlert(marginLvl) {
  if (marginLvl === null || marginLvl === undefined) return null;
  if (marginLvl < 100) return 'danger';
  if (marginLvl < 150) return 'warning';
  return null;
}

const ALERT = {
  warning: {
    border: 'var(--accent-warning)',
    bg: 'color-mix(in srgb, var(--accent-warning) 8%, transparent)',
    headerBg: 'color-mix(in srgb, var(--accent-warning) 10%, var(--bg-secondary))',
    rowBg: 'color-mix(in srgb, var(--accent-warning) 5%, transparent)',
    avatarBg: 'color-mix(in srgb, var(--accent-warning) 18%, transparent)',
    avatarColor: 'var(--accent-warning)',
    labelKey: 'trading.marginCall',
    color: 'var(--accent-warning)',
  },
  danger: {
    border: 'var(--accent-danger)',
    bg: 'color-mix(in srgb, var(--accent-danger) 8%, transparent)',
    headerBg: 'color-mix(in srgb, var(--accent-danger) 12%, var(--bg-secondary))',
    rowBg: 'color-mix(in srgb, var(--accent-danger) 6%, transparent)',
    avatarBg: 'color-mix(in srgb, var(--accent-danger) 18%, transparent)',
    avatarColor: 'var(--accent-danger)',
    labelKey: 'trading.stopOutRisk',
    color: 'var(--accent-danger)',
  },
};

// ─── Group items by account ───────────────────────────────────────────────────
function groupByTrader(items) {
  const map = new Map();
  for (const item of items) {
    const key = item.account_id ?? item.account_number ?? 'unknown';
    if (!map.has(key)) {
      map.set(key, {
        account_id: item.account_id,
        account_number: item.account_number,
        email: item.email,
        first_name: item.first_name,
        last_name: item.last_name,
        trades: [],
      });
    }
    map.get(key).trades.push(item);
  }
  return Array.from(map.values());
}

// ─── Mini stat block ──────────────────────────────────────────────────────────
function Stat({ label, value, valueColor, mono = true }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-widest mb-0.5 font-semibold"
        style={{ color: 'var(--text-tertiary)' }}>{label}</p>
      <p className={`text-xs font-bold truncate ${mono ? 'font-mono' : ''}`}
        style={{ color: valueColor ?? 'var(--text-primary)' }}>{value}</p>
    </div>
  );
}

// ─── Live account snapshot panel ─────────────────────────────────────────────
function TraderAccountPanel({ group, accountSnapshots, traderPnL, tradeCount }) {
  const { t } = useLocale();
  const snap = accountSnapshots?.get(group.account_id);

  const balance = snap?.balance ?? null;
  const equity = snap?.equity ?? null;
  const credit = snap?.credit ?? 0;
  const marginUsed = snap?.margin_used ?? 0;
  const freeMargin = snap?.free_margin ?? null;
  const marginLvl = snap?.margin_level ?? null;
  const isLive = !!snap;
  const alert = marginAlert(marginLvl);
  const alertStyle = alert ? ALERT[alert] : null;

  const enginePnL = snap?.positions
    ? snap.positions.reduce((s, p) => s + parseFloat(p.unrealized_pnl || 0), 0)
    : null;
  const displayPnL = enginePnL ?? traderPnL;

  const marginColor =
    marginLvl === null ? 'var(--text-secondary)' :
      marginLvl < 100 ? 'var(--accent-danger)' :
        marginLvl < 200 ? 'var(--accent-warning)' :
          'var(--accent-success)';

  return (
    <div className="rounded-xl p-3 mt-2" style={{
      background: alertStyle
        ? alertStyle.bg
        : isLive
          ? 'color-mix(in srgb, var(--accent-primary) 5%, var(--bg-tertiary))'
          : 'var(--bg-tertiary)',
      border: `1px solid ${alertStyle
        ? alertStyle.border
        : isLive
          ? 'color-mix(in srgb, var(--accent-primary) 20%, transparent)'
          : 'var(--border-color)'}`,
    }}>
      <div className="flex items-center gap-2 mb-2.5">
        {alertStyle
          ? <ShieldAlert className="w-3 h-3 flex-shrink-0 animate-pulse" style={{ color: alertStyle.color }} />
          : <Layers className="w-3 h-3 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
        }
        <span className="text-[9px] font-bold uppercase tracking-widest"
          style={{ color: alertStyle ? alertStyle.color : 'var(--accent-primary)' }}>
          {alertStyle ? t(alertStyle.labelKey) : t('trading.accountState')}
        </span>
        {alertStyle ? (
          <span className="ml-auto flex items-center gap-1 text-[9px] font-black px-1.5 py-0.5 rounded-full animate-pulse"
            style={{ backgroundColor: `color-mix(in srgb, ${alertStyle.border} 20%, transparent)`, color: alertStyle.color }}>
            <ShieldAlert className="w-2 h-2" /> {parseFloat(marginLvl).toFixed(1)}%
          </span>
        ) : isLive ? (
          <span className="ml-auto flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full"
            style={{ backgroundColor: 'color-mix(in srgb, var(--accent-success) 15%, transparent)', color: 'var(--accent-success)' }}>
            <Activity className="w-2 h-2" /> {t('trading.live')}
          </span>
        ) : (
          <span className="ml-auto text-[9px] font-bold" style={{ color: 'var(--text-tertiary)' }}>{t('trading.snapshot')}</span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-x-4 gap-y-2.5">
        {balance !== null && <Stat label={t('trading.balance')} value={`$${fmt(balance)}`} />}
        {equity !== null && (
          <Stat label={t('trading.equity')} value={`$${fmt(equity)}`}
            valueColor={balance !== null
              ? parseFloat(equity) >= parseFloat(balance) ? 'var(--accent-success)' : 'var(--accent-danger)'
              : undefined} />
        )}
        {displayPnL !== null && (
          <Stat label={t('trading.floatPnL')}
            value={`${displayPnL >= 0 ? '+' : ''}$${fmt(displayPnL)}`}
            valueColor={pnlColor(displayPnL)} />
        )}
        {parseFloat(credit) > 0 && <Stat label={t('trading.credit')} value={`$${fmt(credit)}`} valueColor="var(--accent-info)" />}
        {freeMargin !== null && <Stat label={t('trading.freeMargin')} value={`$${fmt(freeMargin)}`} />}
        {marginUsed > 0 && <Stat label={t('trading.marginUsed')} value={`$${fmt(marginUsed)}`} />}
        {marginLvl !== null && <Stat label={t('trading.marginLvl')} value={`${parseFloat(marginLvl).toFixed(1)}%`} valueColor={marginColor} />}
        <Stat label={t('trading.openTrades')} value={tradeCount} />
      </div>
    </div>
  );
}

// ─── Desktop: single trade row inside a trader group ─────────────────────────
function TradeRow({ item, type, onClose, onModify, onCancel, marginLvl }) {
  const { t } = useLocale();
  const ss = sideStyle(item.side);
  const pnl = parseFloat(item.pnl_currency || 0);
  const alert = marginAlert(marginLvl);
  const aStyle = alert ? ALERT[alert] : null;

  return (
    <tr className="hover:bg-[var(--bg-tertiary)] transition-all"
      style={{
        borderTop: '1px solid color-mix(in srgb, var(--border-color) 50%, transparent)',
        backgroundColor: aStyle ? aStyle.rowBg : 'transparent',
        boxShadow: aStyle ? `inset 3px 0 0 ${aStyle.border}` : 'none',
      }}>
      <td className="pl-10 pr-4 py-3 whitespace-nowrap">
        <span className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{item.symbol}</span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold"
          style={{ backgroundColor: `color-mix(in srgb, ${ss.bg} 15%, transparent)`, color: ss.color }}>
          {item.side === 'buy' ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {item.side?.toUpperCase()}
        </span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap font-mono text-sm">{(item.volume_lots ?? parseFloat(item.volume)).toFixed(2)}</td>
      <td className="px-4 py-3 whitespace-nowrap font-mono text-sm">
        {type === 'positions'
          ? (item.open_price_units / Math.pow(10, item.price_scale || 5)).toFixed(item.price_scale || 5)
          : type === 'orders'
            ? (item.trigger_price_units / Math.pow(10, item.price_scale || 2)).toFixed(item.price_scale || 2)
            : parseFloat(item.open_price || 0).toFixed(5)}
      </td>

      {/* ── POSITIONS columns ── */}
      {type === 'positions' && (
        <>
          <td className="px-4 py-3 whitespace-nowrap font-mono text-sm">
            {(item.current_price_units / Math.pow(10, item.price_scale || 5)).toFixed(item.price_scale || 5)}
          </td>
          <td className="px-4 py-3 whitespace-nowrap text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>
            <div>{item.stop_loss_units ? `SL ${(item.stop_loss_units / Math.pow(10, item.price_scale || 5)).toFixed(item.price_scale || 5)}` : '—'}</div>
            <div>{item.take_profit_units ? `TP ${(item.take_profit_units / Math.pow(10, item.price_scale || 5)).toFixed(item.price_scale || 5)}` : '—'}</div>
          </td>
          <td className="px-4 py-3 whitespace-nowrap">
            <span className="font-mono font-bold text-sm" style={{ color: pnlColor(pnl) }}>
              {pnl >= 0 ? '+' : ''}${fmt(pnl)}
            </span>
          </td>
        </>
      )}

      {/* ── ORDERS columns ── */}
      {type === 'orders' && (
        <>
          <td className="px-4 py-3 whitespace-nowrap">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium capitalize"
              style={{ backgroundColor: 'color-mix(in srgb, var(--accent-info) 15%, transparent)', color: 'var(--accent-info)' }}>
              {item.type}
            </span>
          </td>
          <td className="px-4 py-3 whitespace-nowrap">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium"
              style={{ backgroundColor: 'color-mix(in srgb, var(--accent-warning) 15%, transparent)', color: 'var(--accent-warning)' }}>
              <Clock className="w-3 h-3" />{item.status}
            </span>
          </td>
          <td className="px-4 py-3 whitespace-nowrap text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>
            <div>Margin: ${fmt(item.reserved_margin)}</div>
            <div>Comm: ${fmt(item.reserved_commission)}</div>
          </td>
          <td className="px-4 py-3 whitespace-nowrap text-xs" style={{ color: 'var(--text-secondary)' }}>
            {new Date(item.created_at).toLocaleString()}
          </td>
        </>
      )}

      {/* ── HISTORY columns ── */}
      {type === 'history' && (
        <>
          <td className="px-4 py-3 whitespace-nowrap font-mono text-sm">
            {parseFloat(item.close_price || 0).toFixed(5)}
          </td>
          <td className="px-4 py-3 whitespace-nowrap text-xs" style={{ color: 'var(--text-secondary)' }}>
            <div>Comm: ${parseFloat(item.commission || 0).toFixed(2)}</div>
            <div>Swap: ${parseFloat(item.swap || 0).toFixed(2)}</div>
          </td>
          <td className="px-4 py-3 whitespace-nowrap">
            <span className="font-mono font-bold text-sm" style={{ color: pnlColor(item.net_profit) }}>
              {parseFloat(item.net_profit || 0) >= 0 ? '+' : ''}${fmt(item.net_profit)}
            </span>
            <div className="text-[10px] font-mono" style={{ color: 'var(--text-tertiary)' }}>
              P&L ${fmt(item.profit_loss)}
            </div>
          </td>
          <td className="px-4 py-3 whitespace-nowrap">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold"
              style={{ backgroundColor: 'color-mix(in srgb, var(--text-tertiary) 12%, transparent)', color: 'var(--text-tertiary)' }}>
              {item.close_reason || '—'}
            </span>
          </td>
          <td className="px-4 py-3 whitespace-nowrap text-xs" style={{ color: 'var(--text-secondary)' }}>
            <div>{new Date(item.opened_at || item.created_at).toLocaleString()}</div>
            <div style={{ color: 'var(--text-tertiary)' }}>{new Date(item.closed_at).toLocaleString()}</div>
            <div className="text-[10px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
              {formatDuration(item.opened_at || item.created_at, item.closed_at)}
            </div>
          </td>
        </>
      )}

      {/* ── Actions ── */}
      <td className="px-4 py-3 whitespace-nowrap text-right">
        {type === 'positions' && (
          <div className="flex gap-1.5 justify-end">
            <button onClick={() => onModify(item)} title={t('trading.modifyPosition')}
              className="p-1.5 rounded-lg transition-all hover:scale-105"
              style={{ backgroundColor: 'color-mix(in srgb, var(--accent-primary) 12%, transparent)', color: 'var(--accent-primary)' }}>
              <Settings className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => onClose(item)} title={t('trading.closePosition')}
              className="p-1.5 rounded-lg transition-all hover:scale-105"
              style={{ backgroundColor: 'color-mix(in srgb, var(--accent-danger) 12%, transparent)', color: 'var(--accent-danger)' }}>
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        {type === 'orders' && (
          <button onClick={() => onCancel(item)}
            className="px-2.5 py-1 rounded-lg text-xs font-bold transition-all hover:opacity-80"
            style={{ backgroundColor: 'color-mix(in srgb, var(--accent-danger) 15%, transparent)', color: 'var(--accent-danger)' }}>
            {t('common.cancel')}
          </button>
        )}
      </td>
    </tr>
  );
}

// ─── Desktop: full trader group (header + account panel + trade rows) ─────────
function TraderGroup({ group, type, accountSnapshots, onClose, onModify, onCancel }) {
  const { t } = useLocale();
  const [collapsed, setCollapsed] = useState(false);

  const traderPnL = group.trades.reduce((s, t) => s + parseFloat(t.pnl_currency || 0), 0);
  const snap = accountSnapshots?.get(group.account_id);
  const isLive = !!snap;
  const marginLvl = snap?.margin_level ?? null;
  const alert = marginAlert(marginLvl);
  const alertStyle = alert ? ALERT[alert] : null;

  // positions=9, history=10 (6 base + open/close/fees/netpnl/reason/dates), orders=7
  const colCount = type === 'positions' ? 9 : type === 'history' ? 10 : 10;

  return (
    <tbody>
      {/* Trader header row */}
      <tr className="cursor-pointer select-none"
        onClick={() => setCollapsed(c => !c)}
        style={{
          backgroundColor: alertStyle
            ? alertStyle.headerBg
            : 'color-mix(in srgb, var(--accent-primary) 4%, var(--bg-secondary))',
          boxShadow: alertStyle ? `inset 3px 0 0 ${alertStyle.border}` : 'none',
        }}>
        <td colSpan={colCount} className="px-6 py-0">
          <div className="flex items-start gap-3 py-3">
            {/* Avatar */}
            <div className="relative flex-shrink-0 mt-0.5">
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-black"
                style={{
                  backgroundColor: alertStyle
                    ? alertStyle.avatarBg
                    : 'color-mix(in srgb, var(--accent-primary) 18%, transparent)',
                  color: alertStyle ? alertStyle.avatarColor : 'var(--accent-primary)',
                  boxShadow: alertStyle ? `0 0 0 2px ${alertStyle.border}` : 'none',
                }}>
                {alertStyle
                  ? <ShieldAlert className="w-4 h-4" />
                  : (group.first_name?.[0] ?? group.email?.[0] ?? '?').toUpperCase()
                }
              </div>
              {isLive && !alertStyle && (
                <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2"
                  style={{ backgroundColor: 'var(--accent-success)', borderColor: 'var(--bg-secondary)' }} />
              )}
              {alertStyle && (
                <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 animate-ping"
                  style={{ backgroundColor: alertStyle.border, borderColor: 'var(--bg-secondary)' }} />
              )}
            </div>

            {/* Identity + account panel */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-black" style={{ color: 'var(--text-primary)' }}>
                  {group.first_name && group.last_name
                    ? `${group.first_name} ${group.last_name}`
                    : group.email ?? group.account_number}
                </span>
                <span className="text-xs font-mono px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: 'color-mix(in srgb, var(--text-tertiary) 12%, transparent)', color: 'var(--text-tertiary)' }}>
                  {group.account_number}
                </span>
                {group.email && group.first_name && (
                  <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{group.email}</span>
                )}
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: 'color-mix(in srgb, var(--accent-primary) 12%, transparent)', color: 'var(--accent-primary)' }}>
                  {group.trades.length} {type}{group.trades.length !== 1 ? 's' : ''}
                </span>
                {type === 'positions' && (
                  <span className="text-xs font-mono font-bold" style={{ color: pnlColor(traderPnL) }}>
                    {traderPnL >= 0 ? '+' : ''}${fmt(traderPnL)} {t('trading.pnl')}
                  </span>
                )}
                {alertStyle && (
                  <span className="flex items-center gap-1 text-[10px] font-black px-2 py-0.5 rounded-full animate-pulse"
                    style={{
                      backgroundColor: `color-mix(in srgb, ${alertStyle.border} 18%, transparent)`,
                      color: alertStyle.color,
                      border: `1px solid color-mix(in srgb, ${alertStyle.border} 40%, transparent)`,
                    }}>
                    <ShieldAlert className="w-2.5 h-2.5" />
                    {t(alertStyle.labelKey)} · {parseFloat(marginLvl).toFixed(1)}%
                  </span>
                )}
              </div>
              <TraderAccountPanel
                group={group}
                accountSnapshots={accountSnapshots}
                traderPnL={traderPnL}
                tradeCount={group.trades.length}
              />
            </div>

            <ChevronDown className="w-4 h-4 flex-shrink-0 mt-1 transition-transform duration-200"
              style={{ color: 'var(--text-tertiary)', transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }} />
          </div>
        </td>
      </tr>

      {/* Per-group column headers */}
      {!collapsed && (
        <tr style={{ backgroundColor: 'color-mix(in srgb, var(--bg-tertiary) 80%, transparent)' }}>
          <th className="pl-10 pr-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider min-w-[120px]"
            style={{ color: 'var(--text-tertiary)' }}>{t('trading.symbol')}</th>
          <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider"
            style={{ color: 'var(--text-tertiary)' }}>{t('trading.side')}</th>
          <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider"
            style={{ color: 'var(--text-tertiary)' }}>{t('trading.volume')}</th>
          <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider"
            style={{ color: 'var(--text-tertiary)' }}>
            {type === 'history' ? t('trading.openPrice') : type === 'orders' ? t('trading.price') : t('trading.openPrice')}
          </th>
          {type === 'positions' && (
            <>
              <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider"
                style={{ color: 'var(--text-tertiary)' }}>{t('trading.currentPrice')}</th>
              <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider"
                style={{ color: 'var(--text-tertiary)' }}>{t('trading.slTp')}</th>
              <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider"
                style={{ color: 'var(--text-tertiary)' }}>{t('trading.pnl')}</th>
            </>
          )}
          {type === 'orders' && (
            <>
              <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider"
                style={{ color: 'var(--text-tertiary)' }}>{t('common.type')}</th>
              <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider"
                style={{ color: 'var(--text-tertiary)' }}>{t('common.status')}</th>
              <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider"
                style={{ color: 'var(--text-tertiary)' }}>{t('trading.reserved')}</th>
              <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider"
                style={{ color: 'var(--text-tertiary)' }}>{t('trading.created')}</th>
            </>
          )}
          {type === 'history' && (
            <>
              <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider"
                style={{ color: 'var(--text-tertiary)' }}>{t('trading.closePrice')}</th>
              <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider"
                style={{ color: 'var(--text-tertiary)' }}>{t('trading.fees')}</th>
              <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider"
                style={{ color: 'var(--text-tertiary)' }}>{t('trading.netPnL')}</th>
              <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider"
                style={{ color: 'var(--text-tertiary)' }}>{t('trading.reason')}</th>
              <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider"
                style={{ color: 'var(--text-tertiary)' }}>{t('trading.openedClosed')}</th>
            </>
          )}
          <th className="px-4 py-2 text-right text-[10px] font-bold uppercase tracking-wider"
            style={{ color: 'var(--text-tertiary)' }}>{t('common.actions')}</th>
        </tr>
      )}

      {/* Trade rows */}
      {!collapsed && group.trades.map((item, idx) => (
        <TradeRow
          key={item.id ?? idx}
          item={item}
          type={type}
          onClose={onClose}
          onModify={onModify}
          onCancel={onCancel}
          marginLvl={marginLvl}
        />
      ))}
    </tbody>
  );
}

// ─── Mobile: single trade card ────────────────────────────────────────────────
function TradeCard({ item, type, expanded, onToggle, onClose, onModify, onCancel }) {
  const { t } = useLocale();
  const isOpen = expanded === item.id;
  const ss = sideStyle(item.side);
  const pnl = parseFloat(item.pnl_currency || 0);

  return (
    <div className="rounded-xl border overflow-hidden"
      style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'color-mix(in srgb, var(--border-color) 70%, transparent)' }}>
      <button className="w-full flex items-center gap-3 px-3.5 py-3 text-left" onClick={() => onToggle(item.id)}>
        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `color-mix(in srgb, ${ss.bg} 15%, transparent)`, color: ss.color }}>
          {item.side === 'buy' ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{item.symbol}</p>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-bold uppercase" style={{ color: ss.color }}>{item.side}</span>
            <span className="text-[10px] font-mono" style={{ color: 'var(--text-tertiary)' }}>
              {t('trading.volume')}: {(item.volume_lots ?? parseFloat(item.volume)).toFixed(2)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {type === 'positions' && (
            <span className="text-sm font-black font-mono" style={{ color: pnlColor(pnl) }}>
              {pnl >= 0 ? '+' : ''}${fmt(pnl)}
            </span>
          )}
          {type === 'history' && (
            <span className="text-sm font-black font-mono" style={{ color: pnlColor(item.net_profit) }}>
              {parseFloat(item.net_profit || 0) >= 0 ? '+' : ''}${fmt(item.net_profit)}
            </span>
          )}
          <ChevronDown className="w-3.5 h-3.5 transition-transform duration-200"
            style={{ color: 'var(--text-tertiary)', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
        </div>
      </button>

      {isOpen && (
        <div className="border-t px-3.5 py-3 space-y-2.5"
          style={{ borderColor: 'var(--border-color)', backgroundColor: 'color-mix(in srgb, var(--bg-secondary) 60%, transparent)' }}>
          <div className="grid grid-cols-2 gap-2">

            {/* ── POSITIONS detail ── */}
            {type === 'positions' && (
              <>
                <div>
                  <p className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{t('trading.openPrice')}</p>
                  <p className="text-xs font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {(item.open_price_units / Math.pow(10, item.price_scale || 5)).toFixed(item.price_scale || 5)}
                  </p>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{t('trading.currentPrice')}</p>
                  <p className="text-xs font-mono" style={{ color: 'var(--text-primary)' }}>
                    {(item.current_price_units / Math.pow(10, item.price_scale || 5)).toFixed(item.price_scale || 5)}
                  </p>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{t('trading.stopLoss')}</p>
                  <p className="text-xs font-mono" style={{ color: 'var(--text-primary)' }}>
                    {item.stop_loss_units ? (item.stop_loss_units / Math.pow(10, item.price_scale || 5)).toFixed(item.price_scale || 5) : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{t('trading.takeProfit')}</p>
                  <p className="text-xs font-mono" style={{ color: 'var(--text-primary)' }}>
                    {item.take_profit_units ? (item.take_profit_units / Math.pow(10, item.price_scale || 5)).toFixed(item.price_scale || 5) : '—'}
                  </p>
                </div>
              </>
            )}

            {/* ── ORDERS detail ── */}
            {type === 'orders' && (
              <>
                <div>
                  <p className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{t('trading.triggerPrice')}</p>
                  <p className="text-xs font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {(item.trigger_price_units / Math.pow(10, item.price_scale || 2)).toFixed(item.price_scale || 2)}
                  </p>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{t('common.type')}</p>
                  <p className="text-xs font-semibold capitalize" style={{ color: 'var(--accent-info)' }}>{item.type}</p>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{t('common.status')}</p>
                  <p className="text-xs font-semibold" style={{ color: 'var(--accent-warning)' }}>{item.status}</p>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{t('trading.reservedMargin')}</p>
                  <p className="text-xs font-mono">${fmt(item.reserved_margin)}</p>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{t('trading.commission')}</p>
                  <p className="text-xs font-mono">${fmt(item.reserved_commission)}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{t('trading.created')}</p>
                  <p className="text-xs">{new Date(item.created_at).toLocaleString()}</p>
                </div>
              </>
            )}

            {/* ── HISTORY detail ── */}
            {type === 'history' && (
              <>
                <div>
                  <p className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{t('trading.openPrice')}</p>
                  <p className="text-xs font-mono">{parseFloat(item.open_price || 0).toFixed(5)}</p>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{t('trading.closePrice')}</p>
                  <p className="text-xs font-mono">{parseFloat(item.close_price || 0).toFixed(5)}</p>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{t('trading.netPnL')}</p>
                  <p className="text-xs font-mono font-bold" style={{ color: pnlColor(item.net_profit) }}>
                    {parseFloat(item.net_profit || 0) >= 0 ? '+' : ''}${fmt(item.net_profit)}
                  </p>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{t('trading.grossPnL')}</p>
                  <p className="text-xs font-mono" style={{ color: pnlColor(item.profit_loss) }}>
                    {parseFloat(item.profit_loss || 0) >= 0 ? '+' : ''}${fmt(item.profit_loss)}
                  </p>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{t('trading.commission')}</p>
                  <p className="text-xs font-mono">${fmt(item.commission)}</p>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{t('trading.swap')}</p>
                  <p className="text-xs font-mono">${fmt(item.swap)}</p>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{t('trading.reason')}</p>
                  <p className="text-xs font-semibold">{item.close_reason || '—'}</p>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{t('trading.duration')}</p>
                  <p className="text-xs">{formatDuration(item.opened_at || item.created_at, item.closed_at)}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{t('trading.openedAt')}</p>
                  <p className="text-xs">{new Date(item.opened_at || item.created_at).toLocaleString()}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{t('trading.closedAt')}</p>
                  <p className="text-xs">{new Date(item.closed_at).toLocaleString()}</p>
                </div>
              </>
            )}
          </div>

          {/* ── Action buttons ── */}
          {type === 'positions' && (
            <div className="flex gap-2">
              <button onClick={() => onModify(item)}
                className="flex-1 py-2 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 active:scale-95 transition-all"
                style={{ backgroundColor: 'color-mix(in srgb, var(--accent-primary) 12%, transparent)', color: 'var(--accent-primary)', border: '1px solid color-mix(in srgb, var(--accent-primary) 25%, transparent)' }}>
                <Settings className="w-3.5 h-3.5" /> {t('common.edit')}
              </button>
              <button onClick={() => onClose(item)}
                className="flex-1 py-2 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 active:scale-95 transition-all"
                style={{ backgroundColor: 'color-mix(in srgb, var(--accent-danger) 12%, transparent)', color: 'var(--accent-danger)', border: '1px solid color-mix(in srgb, var(--accent-danger) 25%, transparent)' }}>
                <X className="w-3.5 h-3.5" /> {t('trading.closePosition')}
              </button>
            </div>
          )}
          {type === 'orders' && (
            <button onClick={() => onCancel(item)}
              className="w-full py-2 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 active:scale-95 transition-all"
              style={{ backgroundColor: 'color-mix(in srgb, var(--accent-danger) 12%, transparent)', color: 'var(--accent-danger)', border: '1px solid color-mix(in srgb, var(--accent-danger) 25%, transparent)' }}>
              <X className="w-3.5 h-3.5" /> {t('trading.cancelOrder')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Mobile: trader group card ────────────────────────────────────────────────
function MobileTraderGroup({ group, type, accountSnapshots, onClose, onModify, onCancel }) {
  const { t } = useLocale();
  const [collapsed, setCollapsed] = useState(false);
  const [expandedTrade, setExpandedTrade] = useState(null);

  const traderPnL = group.trades.reduce((s, tr) => s + parseFloat(tr.pnl_currency || 0), 0);
  const snap = accountSnapshots?.get(group.account_id);
  const isLive = !!snap;
  const marginLvl = snap?.margin_level ?? null;
  const alert = marginAlert(marginLvl);
  const alertStyle = alert ? ALERT[alert] : null;

  return (
    <div className="rounded-2xl border overflow-hidden"
      style={{
        backgroundColor: 'var(--bg-secondary)',
        borderColor: alertStyle ? alertStyle.border : 'var(--border-color)',
        boxShadow: alertStyle ? `0 0 0 1px ${alertStyle.border}` : 'none',
      }}>

      {/* Alert banner */}
      {alertStyle && (
        <div className="flex items-center gap-2 px-4 py-2 animate-pulse"
          style={{ backgroundColor: `color-mix(in srgb, ${alertStyle.border} 15%, transparent)` }}>
          <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0" style={{ color: alertStyle.color }} />
          <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: alertStyle.color }}>
            {t(alertStyle.labelKey)}
          </span>
          <span className="ml-auto text-[10px] font-black font-mono" style={{ color: alertStyle.color }}>
            {parseFloat(marginLvl).toFixed(1)}% {t('trading.marginLvl')}
          </span>
        </div>
      )}

      {/* Trader header */}
      <button className="w-full flex items-center gap-3 px-4 py-4 text-left"
        onClick={() => setCollapsed(c => !c)}>
        <div className="relative flex-shrink-0">
          <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-black"
            style={{
              backgroundColor: alertStyle
                ? alertStyle.avatarBg
                : 'color-mix(in srgb, var(--accent-primary) 15%, transparent)',
              color: alertStyle ? alertStyle.avatarColor : 'var(--accent-primary)',
              boxShadow: alertStyle ? `0 0 0 2px ${alertStyle.border}` : 'none',
            }}>
            {alertStyle
              ? <ShieldAlert className="w-4 h-4" />
              : (group.first_name?.[0] ?? group.email?.[0] ?? '?').toUpperCase()
            }
          </div>
          {isLive && !alertStyle && (
            <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 animate-pulse"
              style={{ backgroundColor: 'var(--accent-success)', borderColor: 'var(--bg-secondary)' }} />
          )}
          {alertStyle && (
            <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 animate-ping"
              style={{ backgroundColor: alertStyle.border, borderColor: 'var(--bg-secondary)' }} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-black truncate" style={{ color: 'var(--text-primary)' }}>
            {group.first_name && group.last_name
              ? `${group.first_name} ${group.last_name}`
              : group.email ?? group.account_number}
          </p>
          <p className="text-[11px] font-mono truncate" style={{ color: 'var(--text-tertiary)' }}>
            {group.account_number}
          </p>
        </div>
        <div className="flex-shrink-0 flex flex-col items-end gap-0.5">
          <span className="text-[11px] font-bold px-2 py-0.5 rounded-full"
            style={{ backgroundColor: 'color-mix(in srgb, var(--accent-primary) 12%, transparent)', color: 'var(--accent-primary)' }}>
            {group.trades.length} {type}
          </span>
          {type === 'positions' && (
            <span className="text-xs font-mono font-bold" style={{ color: pnlColor(traderPnL) }}>
              {traderPnL >= 0 ? '+' : ''}${fmt(traderPnL)}
            </span>
          )}
        </div>
        <ChevronDown className="w-4 h-4 flex-shrink-0 transition-transform duration-200"
          style={{ color: 'var(--text-tertiary)', transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }} />
      </button>

      {/* Account panel */}
      <div className="px-4 pb-3">
        <TraderAccountPanel
          group={group}
          accountSnapshots={accountSnapshots}
          traderPnL={traderPnL}
          tradeCount={group.trades.length}
        />
      </div>

      {/* Trade list */}
      {!collapsed && (
        <div className="border-t px-3 pb-3 pt-2 space-y-1.5"
          style={{ borderColor: 'var(--border-color)' }}>
          <p className="text-[9px] font-bold uppercase tracking-widest px-0.5 mb-2"
            style={{ color: 'var(--text-tertiary)' }}>
            {type === 'history' ? t('trading.closedTrades') : `${t('trading.activeTrades')} ${type}`}
          </p>
          {group.trades.map(item => (
            <TradeCard
              key={item.id}
              item={item}
              type={type}
              expanded={expandedTrade}
              onToggle={id => setExpandedTrade(e => e === id ? null : id)}
              onClose={onClose}
              onModify={onModify}
              onCancel={onCancel}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Filter drawer ────────────────────────────────────────────────────────────
function FilterDrawer({ activeTab, setActiveTab, searchQuery, setSearchQuery, counts, onClose }) {
  const { t } = useLocale();
  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative rounded-t-2xl pt-4 pb-8 z-10 max-h-[80vh] overflow-y-auto"
        style={{ backgroundColor: 'var(--bg-secondary)' }}>
        <div className="w-10 h-1 rounded-full mx-auto mb-4" style={{ backgroundColor: 'var(--border-color)' }} />
        <div className="flex items-center justify-between px-4 mb-5">
          <h3 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>{t('trading.filterTrading')}</h3>
          <button onClick={onClose} className="text-xs font-semibold flex items-center gap-1"
            style={{ color: 'var(--accent-primary)' }}>
            <X className="w-3.5 h-3.5" /> {t('common.close')}
          </button>
        </div>
        <div className="px-4 space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-secondary)' }}>{t('common.search')}</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder={t('trading.traderSymbolAccount')}
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border focus:outline-none text-sm"
                style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-2" style={{ color: 'var(--text-secondary)' }}>{t('common.view')}</label>
            <div className="space-y-2">
              {['positions', 'orders', 'history'].map(tab => (
                <button key={tab} onClick={() => setActiveTab(tab)}
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl border text-sm font-semibold transition-all capitalize"
                  style={{
                    backgroundColor: activeTab === tab ? 'color-mix(in srgb, var(--accent-primary) 12%, transparent)' : 'var(--bg-tertiary)',
                    borderColor: activeTab === tab ? 'color-mix(in srgb, var(--accent-primary) 30%, transparent)' : 'var(--border-color)',
                    color: activeTab === tab ? 'var(--accent-primary)' : 'var(--text-primary)',
                  }}>
                  {tab}
                  <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{counts[tab]}</span>
                </button>
              ))}
            </div>
          </div>
          <button onClick={onClose} className="w-full py-3 rounded-xl text-sm font-bold text-white"
            style={{ backgroundColor: 'var(--accent-primary)' }}>
            {t('common.apply')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN EXPORT ──────────────────────────────────────────────────────────────
export default function Trading() {
  const { t } = useLocale();
  const isMobile = useIsMobile();
  const {
    fetchPositions, fetchOrders, fetchTradeHistory,
    adminClosePosition, adminModifyPosition, adminCancelOrder,
    realTimePositions, realTimeOrders, wsConnected,
    accountSnapshots,
  } = useBroker();

  const [activeTab, setActiveTab] = useState('positions');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);

  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modifyData, setModifyData] = useState({ stopLoss: '', takeProfit: '' });
  const [actionLoading, setActionLoading] = useState(false);

  const [localPositions, setLocalPositions] = useState([]);
  const [localOrders, setLocalOrders] = useState([]);

  const positions = realTimePositions.length > 0 ? realTimePositions : localPositions;
  const orders = realTimeOrders.length > 0 ? realTimeOrders : localOrders;

  useEffect(() => { loadData(); setPage(1); }, [activeTab]);
  useEffect(() => { setPage(1); }, [searchQuery]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'positions') {
        const r = await fetchPositions({ status: 'open', limit: 100 });
        setLocalPositions(r.data.positions || []);
      } else if (activeTab === 'orders') {
        const r = await fetchOrders({ status: 'pending', limit: 100 });
        setLocalOrders(r.data.orders || []);
      } else {
        const r = await fetchTradeHistory({ limit: 100 });
        setHistory(r.data.trades || []);
      }
    } catch {
      toast.error(t('errors.general'));
    } finally {
      setLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    const raw = activeTab === 'positions' ? positions : activeTab === 'orders' ? orders : history;
    const q = searchQuery.toLowerCase();
    if (!q) return raw;
    return raw.filter(item =>
      item.symbol?.toLowerCase().includes(q) ||
      item.account_number?.toLowerCase().includes(q) ||
      item.email?.toLowerCase().includes(q) ||
      `${item.first_name ?? ''} ${item.last_name ?? ''}`.toLowerCase().includes(q)
    );
  }, [activeTab, positions, orders, history, searchQuery]);

  const groupedTraders = useMemo(() => groupByTrader(filteredData), [filteredData]);
  const totalPages = Math.max(1, Math.ceil(groupedTraders.length / PAGE_SIZE));
  const pagedTraders = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return groupedTraders.slice(start, start + PAGE_SIZE);
  }, [groupedTraders, page]);

  const totalPnL = positions.reduce((s, p) => s + parseFloat(p.pnl_currency || 0), 0);
  const traderCount = groupedTraders.length;
  const counts = { positions: positions.length, orders: orders.length, history: history.length };

  const openCloseModal = (item) => { setSelectedItem(item); setShowCloseModal(true); };

  const openModifyModal = (item) => {
    setSelectedItem(item);
    setModifyData({
      stopLoss: item.stop_loss_units ? (item.stop_loss_units / Math.pow(10, item.price_scale || 5)).toFixed(item.price_scale || 5) : '',
      takeProfit: item.take_profit_units ? (item.take_profit_units / Math.pow(10, item.price_scale || 5)).toFixed(item.price_scale || 5) : '',
    });
    setShowModifyModal(true);
  };

  const closeAllModals = () => {
    setShowCloseModal(false);
    setShowModifyModal(false);
    setSelectedItem(null);
    setActionLoading(false);
  };

  const confirmClose = async () => {
    if (!selectedItem || actionLoading) return;
    const positionId = selectedItem.id ?? selectedItem.position_id;
    setActionLoading(true);
    try {
      if (activeTab === 'positions') {
        await adminClosePosition(selectedItem.account_id, positionId);
        toast.success(t('trading.positionClosedSuccess'));
      } else {
        await adminCancelOrder(selectedItem.account_id, positionId);
        toast.success(t('trading.orderCancelledSuccess'));
      }
      closeAllModals();
    } catch {
      toast.error(activeTab === 'positions' ? t('trading.failedToClosePosition') : t('trading.failedToCancelOrder'));
      setActionLoading(false);
    }
  };

  const confirmModify = async () => {
    if (!selectedItem || actionLoading) return;
    const positionId = selectedItem.id ?? selectedItem.position_id;
    if (!positionId) {
      toast.error(t('trading.invalidPosition'));
      return;
    }
    setActionLoading(true);
    try {
      await adminModifyPosition(selectedItem.account_id, positionId, {
        stopLoss: modifyData.stopLoss ? parseFloat(modifyData.stopLoss) : null,
        takeProfit: modifyData.takeProfit ? parseFloat(modifyData.takeProfit) : null,
      });
      toast.success(t('trading.positionModifiedSuccess'));
      closeAllModals();
    } catch {
      toast.error(t('trading.failedToModifyPosition'));
      setActionLoading(false);
    }
  };

  // ── Modals ────────────────────────────────────────────────────────────────
  const closeModalJSX = showCloseModal && selectedItem && (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-primary)] rounded-2xl border border-[var(--border-color)] w-full max-w-md">
        <div className="border-b border-[var(--border-color)] p-6 flex items-center gap-3">
          <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'color-mix(in srgb, var(--accent-danger) 12%, transparent)' }}>
            <AlertCircle className="w-6 h-6" style={{ color: 'var(--accent-danger)' }} />
          </div>
          <div>
            <h2 className="text-xl font-bold">{activeTab === 'positions' ? t('trading.closePosition') : t('trading.cancelOrder')}</h2>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t('trading.thisActionCannotBeUndone')}</p>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-center gap-3 p-3 rounded-xl"
            style={{ backgroundColor: 'color-mix(in srgb, var(--accent-primary) 6%, var(--bg-secondary))' }}>
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-black flex-shrink-0"
              style={{ backgroundColor: 'color-mix(in srgb, var(--accent-primary) 15%, transparent)', color: 'var(--accent-primary)' }}>
              {(selectedItem.first_name?.[0] ?? selectedItem.email?.[0] ?? '?').toUpperCase()}
            </div>
            <div>
              <p className="font-bold" style={{ color: 'var(--text-primary)' }}>
                {selectedItem.first_name} {selectedItem.last_name}
              </p>
              <p className="text-xs font-mono" style={{ color: 'var(--text-tertiary)' }}>{selectedItem.account_number}</p>
              {selectedItem.email && <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{selectedItem.email}</p>}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 p-4 rounded-xl text-sm"
            style={{ backgroundColor: 'var(--bg-secondary)' }}>
            <span style={{ color: 'var(--text-secondary)' }}>{t('trading.symbol')}</span>
            <span className="font-bold">{selectedItem.symbol}</span>
            <span style={{ color: 'var(--text-secondary)' }}>{t('trading.side')}</span>
            <span className="font-bold">{selectedItem.side?.toUpperCase()}</span>
            <span style={{ color: 'var(--text-secondary)' }}>{t('trading.volume')}</span>
            <span className="font-mono">{selectedItem.volume}</span>
            {activeTab === 'positions' && (
              <>
                <span style={{ color: 'var(--text-secondary)' }}>{t('trading.pnl')}</span>
                <span className="font-mono font-bold" style={{ color: pnlColor(selectedItem.pnl_currency) }}>
                  ${fmt(selectedItem.pnl_currency)}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="border-t border-[var(--border-color)] p-6 flex gap-3">
          <button onClick={closeAllModals} disabled={actionLoading}
            className="flex-1 px-6 py-3 rounded-xl font-semibold transition-all hover:opacity-80"
            style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}>
            {t('common.cancel')}
          </button>
          <button onClick={confirmClose} disabled={actionLoading}
            className="flex-1 px-6 py-3 rounded-xl font-bold text-white transition-all hover:opacity-90 flex items-center justify-center gap-2"
            style={{ backgroundColor: 'var(--accent-danger)' }}>
            {actionLoading && <Loader className="w-4 h-4 animate-spin" />}
            {activeTab === 'positions' ? t('trading.closePosition') : t('trading.cancelOrder')}
          </button>
        </div>
      </div>
    </div>
  );

  const modifyModalJSX = showModifyModal && selectedItem && (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-primary)] rounded-2xl border border-[var(--border-color)] w-full max-w-md">
        <div className="border-b border-[var(--border-color)] p-6 flex items-center gap-3">
          <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'color-mix(in srgb, var(--accent-primary) 12%, transparent)' }}>
            <Settings className="w-6 h-6" style={{ color: 'var(--accent-primary)' }} />
          </div>
          <div>
            <h2 className="text-xl font-bold">{t('trading.modifyPosition')}</h2>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              {selectedItem.symbol} · {selectedItem.account_number}
            </p>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-center gap-3 p-3 rounded-xl"
            style={{ backgroundColor: 'color-mix(in srgb, var(--accent-primary) 6%, var(--bg-secondary))' }}>
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-black flex-shrink-0"
              style={{ backgroundColor: 'color-mix(in srgb, var(--accent-primary) 15%, transparent)', color: 'var(--accent-primary)' }}>
              {(selectedItem.first_name?.[0] ?? selectedItem.email?.[0] ?? '?').toUpperCase()}
            </div>
            <div>
              <p className="font-bold" style={{ color: 'var(--text-primary)' }}>
                {selectedItem.first_name} {selectedItem.last_name}
              </p>
              <p className="text-xs font-mono" style={{ color: 'var(--text-tertiary)' }}>{selectedItem.account_number}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 p-4 rounded-xl text-sm"
            style={{ backgroundColor: 'var(--bg-secondary)' }}>
            <span style={{ color: 'var(--text-secondary)' }}>{t('trading.symbol')}</span>
            <span className="font-bold">{selectedItem.symbol}</span>
            <span style={{ color: 'var(--text-secondary)' }}>{t('trading.side')}</span>
            <span className="font-bold">{selectedItem.side?.toUpperCase()}</span>
            <span style={{ color: 'var(--text-secondary)' }}>{t('trading.volume')}</span>
            <span className="font-mono">{selectedItem.volume}</span>
            <span style={{ color: 'var(--text-secondary)' }}>{t('trading.currentPrice')}</span>
            <span className="font-mono">
              {selectedItem.current_price_units
                ? (selectedItem.current_price_units / Math.pow(10, selectedItem.price_scale || 5)).toFixed(selectedItem.price_scale || 5)
                : '—'}
            </span>
            <span style={{ color: 'var(--text-secondary)' }}>{t('trading.pnl')}</span>
            <span className="font-mono font-bold" style={{ color: pnlColor(selectedItem.pnl_currency) }}>
              ${fmt(selectedItem.pnl_currency)}
            </span>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-1.5"
              style={{ color: 'var(--text-secondary)' }}>{t('trading.stopLoss')}</label>
            <input
              type="number" step="0.00001" placeholder={t('trading.enterStopLoss')}
              value={modifyData.stopLoss}
              onChange={e => setModifyData(prev => ({ ...prev, stopLoss: e.target.value }))}
              className="w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none font-mono"
              style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-1.5"
              style={{ color: 'var(--text-secondary)' }}>{t('trading.takeProfit')}</label>
            <input
              type="number" step="0.00001" placeholder={t('trading.enterTakeProfit')}
              value={modifyData.takeProfit}
              onChange={e => setModifyData(prev => ({ ...prev, takeProfit: e.target.value }))}
              className="w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none font-mono"
              style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            />
          </div>
        </div>
        <div className="border-t border-[var(--border-color)] p-6 flex gap-3">
          <button onClick={closeAllModals} disabled={actionLoading}
            className="flex-1 px-6 py-3 rounded-xl font-semibold hover:opacity-80 transition-all"
            style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}>
            {t('common.cancel')}
          </button>
          <button onClick={confirmModify} disabled={actionLoading}
            className="flex-1 px-6 py-3 rounded-xl font-bold text-white hover:opacity-90 transition-all flex items-center justify-center gap-2"
            style={{ backgroundColor: 'var(--accent-primary)' }}>
            {actionLoading && <Loader className="w-4 h-4 animate-spin" />}
            {t('settings.saveChanges')}
          </button>
        </div>
      </div>
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // MOBILE
  // ══════════════════════════════════════════════════════════════════════════
  if (isMobile) {
    return (
      <div className="flex flex-col -mx-4 -my-6 min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
        {/* Hero */}
        <div className="px-4 pt-5 pb-6" style={{
          background: 'linear-gradient(135deg, var(--accent-primary) 0%, color-mix(in srgb, var(--accent-primary) 60%, var(--accent-info)) 100%)'
        }}>
          <div className="flex items-center justify-between mb-3">
            <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.6)' }}>
              {t('trading.tradingManagement')}
            </p>
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold"
              style={{ backgroundColor: wsConnected ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)' }}>
              {wsConnected
                ? <><Wifi className="w-3 h-3" style={{ color: 'rgb(16,185,129)' }} /><span style={{ color: 'rgb(16,185,129)' }}>{t('trading.live')}</span></>
                : <><WifiOff className="w-3 h-3" style={{ color: 'rgb(239,68,68)' }} /><span style={{ color: 'rgb(239,68,68)' }}>{t('trading.offline')}</span></>
              }
            </div>
          </div>
          <p className="text-4xl font-black text-white leading-none font-mono">{traderCount}</p>
          <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.6)' }}>
            {t('trading.traderCount', { count: traderCount, total: filteredData.length, tab: activeTab })}
          </p>
          <div className="grid grid-cols-3 gap-2 mt-4">
            {[
              { label: t('trading.positions'), value: counts.positions },
              { label: t('trading.orders'), value: counts.orders },
              { label: t('trading.pnl'), value: `$${(totalPnL / 1000).toFixed(1)}k` },
            ].map(({ label, value }) => (
              <div key={label} className="rounded-xl px-3 py-2.5 text-center"
                style={{ backgroundColor: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)' }}>
                <p className="text-sm font-black text-white">{value}</p>
                <p className="text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.55)' }}>{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Search bar */}
        <div className="flex items-center gap-2 px-4 py-3 border-b"
          style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-secondary)' }}>
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder={t('trading.traderSymbolAccount')}
              className="w-full pl-9 pr-3 py-2 rounded-xl border text-sm focus:outline-none"
              style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
          </div>
          <button onClick={() => setShowFilters(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-bold capitalize"
            style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
            <SlidersHorizontal className="w-4 h-4" />{activeTab}
          </button>
        </div>

        {/* Count */}
        <div className="px-4 py-2 border-b" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-secondary)' }}>
          <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
            {t('trading.traderCount', { count: groupedTraders.length, total: filteredData.length, tab: activeTab })}
          </p>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: 'var(--accent-primary)', borderTopColor: 'transparent' }} />
          </div>
        ) : groupedTraders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <AlertCircle className="w-10 h-10 mb-3" style={{ color: 'var(--text-tertiary)' }} />
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t('trading.noFound', { tab: activeTab })}</p>
          </div>
        ) : (
          <div className="px-4 pt-3 pb-8 space-y-3">
            {pagedTraders.map(group => (
              <MobileTraderGroup
                key={group.account_id ?? group.account_number}
                group={group}
                type={activeTab}
                accountSnapshots={accountSnapshots}
                onClose={openCloseModal}
                onModify={openModifyModal}
                onCancel={openCloseModal}
              />
            ))}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="flex items-center gap-1 text-sm font-semibold disabled:opacity-30 disabled:cursor-not-allowed"
                  style={{ color: 'var(--accent-primary)' }}>
                  <ChevronLeft className="w-4 h-4" /> {t('common.previous')}
                </button>
                <span className="text-sm" style={{ color: 'var(--text-tertiary)' }}>{page} / {totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="flex items-center gap-1 text-sm font-semibold disabled:opacity-30 disabled:cursor-not-allowed"
                  style={{ color: 'var(--accent-primary)' }}>
                  {t('common.next')} <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {showFilters && (
          <FilterDrawer
            activeTab={activeTab} setActiveTab={setActiveTab}
            searchQuery={searchQuery} setSearchQuery={setSearchQuery}
            counts={counts} onClose={() => setShowFilters(false)}
          />
        )}

        {closeModalJSX}
        {modifyModalJSX}
        <div className="pb-[env(safe-area-inset-bottom)]" />
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // DESKTOP
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold mb-1">{t('trading.tradingManagement')}</h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            {t('trading.traderCount', { count: traderCount, total: filteredData.length, tab: activeTab })}
            {activeTab === 'positions' && (
              <span className="ml-2 font-mono font-bold" style={{ color: pnlColor(totalPnL) }}>
                {totalPnL >= 0 ? '+' : ''}${fmt(totalPnL)} {t('trading.pnl')}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-bold"
          style={{ backgroundColor: wsConnected ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)' }}>
          {wsConnected
            ? <><Wifi className="w-4 h-4" style={{ color: 'rgb(16,185,129)' }} /><span style={{ color: 'rgb(16,185,129)' }}>{t('trading.liveMonitoring')}</span></>
            : <><WifiOff className="w-4 h-4" style={{ color: 'rgb(239,68,68)' }} /><span style={{ color: 'rgb(239,68,68)' }}>{t('trading.disconnected')}</span></>}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: t('trading.activeTraders'), value: traderCount },
          { label: t('trading.openPositions'), value: positions.length },
          { label: t('trading.pendingOrders'), value: orders.length },
        ].map(({ label, value }) => (
          <div key={label} className="p-4 rounded-xl border"
            style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
            <p className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>{label}</p>
            <p className="text-2xl font-black font-mono">{value}</p>
          </div>
        ))}
        <div className="p-4 rounded-xl border"
          style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
          <p className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>{t('trading.totalPnL')}</p>
          <p className="text-2xl font-black font-mono" style={{ color: pnlColor(totalPnL) }}>
            {totalPnL >= 0 ? '+' : ''}${fmt(totalPnL)}
          </p>
        </div>
      </div>

      {/* Tabs + search */}
      <div className="flex items-center gap-4">
        <div className="flex gap-0 border-b flex-1" style={{ borderColor: 'var(--border-color)' }}>
          {['positions', 'orders', 'history'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className="px-4 py-2 font-medium transition-all relative capitalize"
              style={{ color: activeTab === tab ? 'var(--accent-primary)' : 'var(--text-secondary)' }}>
              {tab}
              {activeTab === tab && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: 'var(--accent-primary)' }} />
              )}
            </button>
          ))}
        </div>
        <div className="relative w-72 flex-shrink-0">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-tertiary)' }} />
          <input type="text" placeholder={t('trading.traderSymbolAccount')} value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 rounded-xl border text-sm focus:outline-none"
            style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
        </div>
      </div>

      {/* Grouped table */}
      <div className="rounded-xl border overflow-hidden"
        style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="hidden" aria-hidden="true">
              <tr>
                <th>Symbol</th><th>Side</th><th>Volume</th><th>Open Price</th>
                <th>Col5</th><th>Col6</th><th>Col7</th><th>Col8</th><th>Actions</th>
              </tr>
            </thead>

            {loading ? (
              <tbody>
                <tr>
                  <td colSpan={activeTab === 'positions' ? 9 : activeTab === 'history' ? 10 : 7} className="py-16 text-center">
                    <Loader className="w-8 h-8 animate-spin mx-auto" style={{ color: 'var(--accent-primary)' }} />
                  </td>
                </tr>
              </tbody>
            ) : groupedTraders.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan={activeTab === 'positions' ? 9 : activeTab === 'history' ? 10 : 7} className="py-16 text-center"
                    style={{ color: 'var(--text-secondary)' }}>
                    {t('trading.noFound', { tab: activeTab })}
                  </td>
                </tr>
              </tbody>
            ) : (
              pagedTraders.map(group => (
                <TraderGroup
                  key={group.account_id ?? group.account_number}
                  group={group}
                  type={activeTab}
                  accountSnapshots={accountSnapshots}
                  onClose={openCloseModal}
                  onModify={openModifyModal}
                  onCancel={openCloseModal}
                />
              ))
            )}
          </table>
        </div>
        <Pagination
          page={page}
          totalPages={totalPages}
          total={groupedTraders.length}
          pageSize={PAGE_SIZE}
          onChange={setPage}
        />
      </div>

      {closeModalJSX}
      {modifyModalJSX}
    </div>
  );
}